/** Family plan values used in UI (free | paid | gifted). */
import i18n from '@/i18n/i18n';

export type FamilyPlanUi = 'free' | 'paid' | 'gifted';

export function normalizeFamilyPlan(plan: string | null | undefined): FamilyPlanUi {
  if (plan === 'paid' || plan === 'gifted') return plan;
  return 'free';
}

/** Paid entitlements label for display (gifted reads as a family plan). */
export function getFamilyPlanLabel(plan: string | null | undefined): string {
  const normalized = normalizeFamilyPlan(plan);
  if (normalized === 'gifted') return i18n.t('billing:plan.familyGifted');
  if (normalized === 'paid') return i18n.t('billing:plan.family');
  return i18n.t('billing:plan.free');
}

export function familyHasPaidPlan(plan: string | null | undefined): boolean {
  const normalized = normalizeFamilyPlan(plan);
  return normalized === 'paid' || normalized === 'gifted';
}

/** Short pill text for family cards (CSS uppercases). */
export function getFamilyPlanPillLabel(plan: string | null | undefined): string {
  const normalized = normalizeFamilyPlan(plan);
  if (normalized === 'gifted') return i18n.t('billing:plan.gifted');
  if (normalized === 'paid') return i18n.t('billing:plan.family');
  return i18n.t('billing:plan.free');
}

export function isFamilyOwner(role: string | null | undefined): boolean {
  return role === 'owner';
}

export function isFamilyOwnerOrAdmin(role: string | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Next-step copy when a paid Family plan feature is gated.
 * Only owners can purchase (§5.6); others are directed to ask an owner.
 */
export function getFamilyPlanUpgradeHint(isOwner: boolean): string {
  return isOwner ? i18n.t('billing:upgradeHint.owner') : i18n.t('billing:upgradeHint.member');
}

/** Shape compatible with WeTreeApiError without importing api layer into every caller. */
export type UpgradeAwareApiError = {
  getUserMessage?: () => string;
  message?: string;
  isUpgradeRequired?: () => boolean;
};

/**
 * Split an API error into the server message plus an upgrade hint when the limit/feature requires paid.
 */
export function getUpgradeAwareErrorParts(
  error: UpgradeAwareApiError | null | undefined,
  isOwner: boolean,
  fallback: string
): { message: string; upgradeHint: string | null } {
  const message = error?.getUserMessage?.() ?? error?.message ?? fallback;
  const needsUpgrade = Boolean(error?.isUpgradeRequired?.());
  return {
    message,
    upgradeHint: needsUpgrade ? getFamilyPlanUpgradeHint(isOwner) : null
  };
}

/**
 * Status line under the overview plan pill — explains the tier in plain language.
 * When renewal metadata is available for a paid plan, prefer that over the generic paid blurb.
 */
export function getOverviewPlanStatusLine(params: {
  plan: string | null | undefined;
  isOwner: boolean;
  nextChargeLabel?: string | null;
  billingInterval?: 'month' | 'year' | null;
}): string {
  const normalized = normalizeFamilyPlan(params.plan);
  const nextChargeLabel = params.nextChargeLabel?.trim() || null;
  const billingInterval = params.billingInterval ?? null;

  if (normalized === 'free') {
    return params.isOwner ? i18n.t('billing:planStatus.freeOwner') : i18n.t('billing:planStatus.freeMember');
  }

  if (normalized === 'gifted') {
    return i18n.t('billing:planStatus.gifted');
  }

  // paid
  if (nextChargeLabel) {
    if (billingInterval === 'year') {
      return i18n.t('billing:planStatus.renewsYearly', { date: nextChargeLabel });
    }
    if (billingInterval === 'month') {
      return i18n.t('billing:planStatus.renewsMonthly', { date: nextChargeLabel });
    }
    return i18n.t('billing:planStatus.nextCharge', { date: nextChargeLabel });
  }

  return i18n.t('billing:planStatus.paidDefault');
}
